import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { InternalTransferState } from '@interfaces/state/internal-transfer/internal-transfer.interface';
import { createSelector } from '@ngrx/store';
import { parseStr } from '@shared/helpers';
import {
  AccountTypes,
  Eligible_Joint_Account_Types_Pattern,
  FlowTypes,
  GiftThresholds,
  GrantedRole,
  Move_To_New,
  RegisteredRole,
  RoleTypes,
  TransactionTypesEnum,
} from '@shared/models';
import { getTotalGiftAmount, GiftingState } from '@store/reducers/gifting/gifting.reducer';

import * as fromFeature from '../../reducers';
import {
  getAccountTypeSelection,
  getClientStatusSelection,
  getFlowTypeSelection,
  getIRADesignationSelection,
  getIsClient,
  getIsIndividualAccount,
  getIsInheritedIraFlow,
  getIsIRAAccount,
  getIsIRAExecutor,
  getIsIRASingleExecutor,
  getIsIRAToEstateFlow,
  getIsIRATrust,
  getIsJointAccount,
  getIsMultiInheritanceFlow,
  getIsNonVanguardGiftingFlow,
  getIsOnlyTrustee,
  getIsOther,
  getIsProspect,
  getIsScaleGifting,
  getIsSpouse,
  getIsTrustAccount,
  getIsTrusteeOrg,
  getIsTrustToTrust,
  getIsTrustToTrustFlow,
  getIsVanguardGiftingFlow,
  getSelectExperimentGroup,
  getSpouseStatusSelection,
  getTypeOfTrust,
  TriageState,
} from '../../reducers/triage/triage.reducer';
import { selectGiftingState } from '../gifting/gifting.selectors';
import {
  selectInternalTransferState,
  selectInternalTransferTransactionType,
} from '../internal-transfer/internal-transfer.selectors';
import { selectTransactionType } from '../selected-client-accounts/selected-client-accounts.selectors';
import { selectIsNewAccountTransactionExist } from '../transaction-state/transaction-state.selectors';

export const selectTriageState = createSelector(fromFeature.getTriageState, (state) => state);

export const clientStatusSelection = createSelector(selectTriageState, getClientStatusSelection);
export const spouseStatusSelection = createSelector(selectTriageState, getSpouseStatusSelection);
export const flowTypeSelection = createSelector(selectTriageState, getFlowTypeSelection);
export const selectAccountType = createSelector(selectTriageState, getAccountTypeSelection);
export const selectExperimentGroup = createSelector(selectTriageState, getSelectExperimentGroup);
export const selectIRADesignation = createSelector(selectTriageState, getIRADesignationSelection);
export const selectIndividualDesignation = createSelector(
  selectTriageState,
  (state): IndividualDesignationListedTypes => state.individualDesignation,
);

export const selectIsIndividualDesignationExecutor = createSelector(
  selectTriageState,
  (state) => state.individualDesignation === IndividualDesignationListedTypes.EXECUTOR,
);

export const isEligibleClient = (state: TriageState) => {
  return getIsClient(state) && !getIsOther(state);
};

export const isEligibleProspect = (state: TriageState) => {
  return getIsProspect(state) && (getIsIRAAccount(state) || getIsIndividualAccount(state));
};

export const isGiftingFlowUsingToggle = (state: TriageState) =>
  getIsVanguardGiftingFlow(state) || getIsNonVanguardGiftingFlow(state);
export const allowedGiftingFlows = (state: TriageState) =>
  getIsIndividualAccount(state) || getIsScaleGifting(state);

export const selectIsClient = createSelector(selectTriageState, getIsClient);
export const selectIsProspect = createSelector(selectTriageState, getIsProspect);
export const selectIsSpouse = createSelector(selectTriageState, getIsSpouse);

export const selectIsEligibleClient = createSelector(selectTriageState, isEligibleClient);
export const selectIsEligibleProspect = createSelector(selectTriageState, isEligibleProspect);

export const selectIsIndvAccount = createSelector(selectTriageState, getIsIndividualAccount);
export const selectIsJointAccount = createSelector(selectTriageState, getIsJointAccount);
export const selectIsTrustAccount = createSelector(selectTriageState, getIsTrustAccount);
export const selectIsIraAccount = createSelector(selectTriageState, getIsIRAAccount);

export const selectIsGiftingFlowUsingToggle = createSelector(
  selectTriageState,
  isGiftingFlowUsingToggle,
);
export const selectAllowedGiftingFlows = createSelector(
  selectTriageState,
  (triageState: TriageState) =>
    isGiftingFlowUsingToggle(triageState) && allowedGiftingFlows(triageState),
);

export const selectIsMXMForExecutor = createSelector(
  selectInternalTransferState,
  (internalTransferState: InternalTransferState) => {
    const isEstateFrom: boolean = internalTransferState.triage.transferFrom === AccountTypes.ESTATE;
    if (!isEstateFrom) return false;

    const accountData = internalTransferState?.transferFrom?.selectedAccount?.accountData;
    const registeredRoles: RegisteredRole[] = accountData?.registeredRoles ?? [];
    const grantedRoles: GrantedRole[] = accountData?.grantedRoles ?? [];

    const uniqueExecutorRoles = Array.from(
      new Map(
        [...registeredRoles, ...grantedRoles]
          .filter((role) => role.roleType === RoleTypes.EXEX || role.roleType === RoleTypes.EXAD)
          .map((role) => [role.poid, role]),
      ).values(),
    );

    return uniqueExecutorRoles.length > 1;
  },
);

//Possibly refactor this to add more flows?
export const selectInternalTransferCanOpenNewAccount = createSelector(
  selectInternalTransferState,
  (internalTransferState: InternalTransferState) => {
    const { transferFrom, transferTo } = internalTransferState.triage;
    return (
      (transferFrom === AccountTypes.INDV || transferFrom === AccountTypes.ESTATE) &&
      transferTo === AccountTypes.INDV
    );
  },
);

export const selectIsJointGiftingApprovalScenario = createSelector(
  selectTriageState,
  selectGiftingState,
  (triageState: TriageState, giftingState: GiftingState) => {
    const accountType = getAccountTypeSelection(triageState);
    const giftAmount = parseStr(getTotalGiftAmount(giftingState));

    if (!!accountType && !!giftAmount && getIsJointAccount(triageState)) {
      return (
        (accountType === AccountTypes.JOINT_TENANTS_WROS && giftAmount > GiftThresholds['500K']) ||
        (Eligible_Joint_Account_Types_Pattern.test(accountType) &&
          accountType !== AccountTypes.JOINT_TENANTS_WROS)
      );
    }

    return false;
  },
);

export const selectIsIRAExecutor = createSelector(selectTriageState, getIsIRAExecutor);
export const selectIsIRASingleExecutor = createSelector(selectTriageState, getIsIRASingleExecutor);
export const selectIsTrusteeOrg = createSelector(selectTriageState, getIsTrusteeOrg);
export const selectIsSingleTrustee = createSelector(selectTriageState, getIsOnlyTrustee);
export const selectIsTrustee = createSelector(selectTriageState, getIsIRATrust);
export const selectisIRAToEstateFlow = createSelector(selectTriageState, getIsIRAToEstateFlow);
export const selectTypeOfTrust = createSelector(selectTriageState, getTypeOfTrust);
export const selectIsTrustToTrust = createSelector(selectTriageState, getIsTrustToTrust);
export const selectIsTrustToTrustFlow = createSelector(selectTriageState, getIsTrustToTrustFlow);
export const selectIsMultiInheritanceFlow = createSelector(
  selectTriageState,
  getIsMultiInheritanceFlow,
);

export const selectIsManualAccountOpeningRequired = createSelector(
  flowTypeSelection,
  selectIsIndividualDesignationExecutor,
  selectIsIRASingleExecutor,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  (
    flowType: FlowTypes | null,
    isIndividualExecutor: boolean,
    isIRAExecutor: boolean,
    isIRATrustee: boolean,
    isTrustToTrust: boolean,
  ) =>
    flowType === FlowTypes.INHERITANCE &&
    (isIndividualExecutor || isIRAExecutor || isIRATrustee || isTrustToTrust),
);

export const selectIsOpeningNewAccountAutomatically = createSelector(
  flowTypeSelection,
  selectTransactionType,
  selectInternalTransferTransactionType,
  selectIsManualAccountOpeningRequired,
  selectIsNewAccountTransactionExist,
  (
    flowType: FlowTypes | null,
    transactionType: TransactionTypesEnum | null,
    internalTransferTransactionType: TransactionTypesEnum | null,
    isManualAccountOpeningRequired: boolean,
    hasNewAccountTransferInMultoInheritance: boolean,
  ): boolean =>
    (flowType === FlowTypes.INTERNAL_TRANSFER &&
      !!internalTransferTransactionType &&
      Move_To_New.has(internalTransferTransactionType)) ||
    (flowType === FlowTypes.MULTI_INHERITANCE && hasNewAccountTransferInMultoInheritance) ||
    (!!transactionType && Move_To_New.has(transactionType) && !isManualAccountOpeningRequired),
);

export const selectIsInheritedIraFlow = createSelector(selectTriageState, getIsInheritedIraFlow);
